import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Grid,
  Paper,
  CircularProgress,
  Link,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Container,
  Fade,
  Grow,
  Slide,
  useMediaQuery,
  MobileStepper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  LockReset as LockResetIcon,
  ArrowForward,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from '@mui/icons-material';
import swal from 'sweetalert';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';

// Internal Logic
import { getIDWorkmanager, getProviderById, encrypt } from './FunctionLogin.js';
import CambiarIdioma from './cambiaridioma.js';
import WhatsAppv1 from './whatsapp.js';
import locations from '../config/locations.js';

// Assets
import logopng from '../recursos/logocompleto.png';
import talsaBuilding from '../recursos/talsa_building.jpg';
import headergif from '../recursos/header.gif';
import proveedorlogo from '../recursos/proveedor.jpg';
import proveedorlogoen from '../recursos/proveedoren.jpg';
import comoserproveedor from '../recursos/comoserproveedor.jpg';
import comoserproveedoren from '../recursos/comoserproveedoren.jpg';
import informacion from '../recursos/informacion.jpg';
import informacionen from '../recursos/informacionen.jpg';
import tutorial from '../recursos/tutorial.jpg';
import tutorialen from '../recursos/tutorialen.jpg';

// --- Theme Definition ---
const loginTheme = createTheme({
  palette: {
    primary: {
      main: '#BD1F34', // Talsa Red
      dark: '#8a1525',
      light: '#e04f5f',
    },
    text: {
      primary: '#2D3436',
      secondary: '#636E72',
    },
    background: {
      default: '#F7F9FC',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontWeight: 700,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '1rem',
      borderRadius: '8px',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '50px', // Pill shape for modern feel
          boxShadow: '0 4px 14px 0 rgba(189, 31, 52, 0.39)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(189, 31, 52, 0.23)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#F7F9FC',
            borderRadius: '12px',
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover fieldset': {
              borderColor: '#e0e0e0',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#BD1F34',
              backgroundColor: 'transparent',
            },
          },
        },
      },
    },
  },
});

// 3D CoverFlow Carousel Component
function CoverFlowCarousel({ cards }) {
  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = cards.length;

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % maxSteps);
    }, 4000);
    return () => clearInterval(timer);
  }, [maxSteps]);

  const handleStep = (index) => {
    setActiveStep(index);
  };

  const getCardStyle = (index) => {
    let normalizeDiff = index - activeStep;
    if (normalizeDiff > maxSteps / 2) normalizeDiff -= maxSteps;
    if (normalizeDiff < -maxSteps / 2) normalizeDiff += maxSteps;

    const isActive = normalizeDiff === 0;
    const isLeft = normalizeDiff < 0;
    const isRight = normalizeDiff > 0;

    let transform = 'translateX(0) scale(1) rotateY(0deg)';
    let zIndex = 10;
    let opacity = 1;

    if (isActive) {
      transform = 'translateX(0) scale(1.1) translateZ(50px)';
      zIndex = 20;
      opacity = 1;
    } else if (isLeft) {
      transform = `translateX(${normalizeDiff * 60}%) scale(0.8) rotateY(25deg)`;
      zIndex = 10 + normalizeDiff;
      opacity = 0.7;
    } else if (isRight) {
      transform = `translateX(${normalizeDiff * 60}%) scale(0.8) rotateY(-25deg)`;
      zIndex = 10 - normalizeDiff;
      opacity = 0.7;
    }

    if (Math.abs(normalizeDiff) > 1) {
      opacity = 0;
      zIndex = 0;
      if (Math.abs(normalizeDiff) > 2) return { display: 'none' };
    }

    return {
      position: 'absolute',
      width: '90%',
      left: '20%',
      top: 20,
      height: '100%',
      transition: 'all 0.5s ease-out',
      transform: transform,
      zIndex: zIndex,
      opacity: opacity,
      cursor: 'pointer',
      boxShadow: isActive ? '0 10px 30px rgba(0,0,0,0.3)' : 'none',
      borderRadius: '16px',
      overflow: 'hidden',
      border: isActive ? '2px solid white' : 'none'
    };
  };

  if (!cards || cards.length === 0) return null;

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: { xs: 160, sm: 220 },
      mt: { xs: 2, sm: 4 },
      mb: 2,
      perspective: '1000px',
      transformStyle: 'preserve-3d',
      display: 'flex',
      justifyContent: 'center'
    }}>
      {cards.map((card, index) => (
        <Paper
          key={index}
          elevation={5}
          onClick={() => handleStep(index)}
          sx={getCardStyle(index)}
        >
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <img
              src={card.image}
              alt={card.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <Box sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
              p: 2,
              color: 'white',
              textAlign: 'center'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{card.title}</Typography>
            </Box>
            {(index === activeStep) && (
              <a href={card.url} target="_blank" rel="noopener noreferrer"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              />
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

export default function Login() {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const isMobile = useMediaQuery(loginTheme.breakpoints.down('md'));

  // State
  const [usuario, setUsuario] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [animate, setAnimate] = useState(false);

  // Estados para Modal de Activación
  const [providerData, setProviderData] = useState(null);

  useEffect(() => {
    setAnimate(true);
  }, []);

  // New API Calls
  const loginWithBigQuery = async (username, password) => {
    try {
      console.log('🔍 Intentando login BigQuery con usuario:', username);

      const response = await fetch('https://consumos.talsa.com.co/api/talsa/proveedores/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      console.log('📡 Respuesta BigQuery status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Login BigQuery exitoso:', data);
        return { success: true, data };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.log('❌ Login BigQuery falló - Status:', response.status, 'Error:', errorData);
        return { success: false, status: response.status, error: errorData };
      }
    } catch (error) {
      console.error('🔥 Error de conexión BigQuery:', error);
      swal(t("login.swal_error_conexion"), t("login.swal_error_conexion_desc"), "error");
      return { success: false, error: error.message };
    }
  };

  const registerUser = async (username, password, email, rol = 'usuario') => {
    try {
      const response = await fetch('https://consumos.talsa.com.co/api/talsa/proveedores/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ usuario: username, contrasena: password, email, rol }),
      });

      if (response.ok) {
        const data = await response.json();
        return { success: true, data };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.error };
      }
    } catch (error) {
      console.error('Error en registro:', error);
      return { success: false, error: 'Error de conexión' };
    }
  };

  // Procesar login exitoso
  const processSuccessfulLogin = async (alertData, bigQueryData) => {
    try {
      // Guardar datos del proveedor (Alert)
      localStorage.setItem("Proveedor", alertData.Proveedor);
      localStorage.setItem("Tproveedor", alertData.Tproveedor);
      localStorage.setItem("Nit", alertData.Nit);
      localStorage.setItem("TipoProveedor", alertData.tipo);
      localStorage.setItem("Pais", alertData.Pais);
      localStorage.setItem("Ciudad", alertData.Ciudad);
      localStorage.setItem("Telefono", alertData.Telefono);

      // Guardar datos de BigQuery
      localStorage.setItem("token", bigQueryData.token);
      localStorage.setItem("Email", bigQueryData.email);
      localStorage.setItem("rol", bigQueryData.rol);

      // Tipo de usuario por defecto
      const encType = await encrypt("PROVEEDOR");
      localStorage.setItem("TipoDeUsuario", encType);

      const session = await encrypt("Conectado" + Math.random().toFixed(2));
      Cookies.set('sessionId', session, { sameSite: 'strict' });
      localStorage.setItem("showOrderPopup", true);

      setIsLoading(false);
      navigate('/proveedores/órdenes');
    } catch (error) {
      console.error('Error procesando login:', error);
      setIsLoading(false);
      swal(t("login.swal_error"), t("login.swal_error_procesar"), "error");
    }
  };

  // Main Login Handler
  const handleLogin = async () => {
    if (!usuario || !contrasena) {
      swal(t("login.titulo"), t("login.swal_validando_diligencia"), "warning");
      return;
    }

    try {
      setIsLoading(true);
      console.log('🚀 Iniciando proceso de login para:', usuario);

      // Paso 1: Validar en el sistema viejo (Alert - SAP)
      console.log('📋 Paso 1: Validando en getProviderById (SAP)...');
      const alertResult = await getProviderById(usuario);

      console.log('📊 Resultado Alert:', alertResult);

      if (alertResult && alertResult.length > 0) {
        const alertData = alertResult[0];
        console.log('✅ Usuario encontrado en Alert (SAP):', alertData);
        setProviderData(alertData);

        // EXTRAER EL ID REAL (CBP_UUID) para usarlo como identificador en BigQuery
        const realUsuario = alertData.Proveedor;
        console.log('🆔 Usando ID real para BigQuery:', realUsuario);

        // Paso 2: Intentar login en BigQuery usando el ID real
        console.log('📋 Paso 2: Intentando login en BigQuery...');
        const bigQueryResult = await loginWithBigQuery(realUsuario, contrasena);

        if (bigQueryResult.success) {
          // Usuario existe en ambos sistemas - Login exitoso
          console.log('🎉 Login exitoso en ambos sistemas');
          await processSuccessfulLogin(alertData, bigQueryResult.data);
        } else if (bigQueryResult.status === 401) {
          // Usuario existe en SAP pero la combinación de cuenta/contraseña no es válida en BQ o no tiene cuenta
          setIsLoading(false);
          swal({
            title: t("login.swal_cuenta_inactiva"),
            text: t("login.swal_cuenta_inactiva_desc"),
            icon: "warning",
            buttons: {
              cancel: t("login.swal_btn_cancelar"),
              confirm: {
                text: t("login.swal_btn_activar"),
                value: "activar"
              }
            },
          }).then((value) => {
            if (value === "activar") {
              navigate('/proveedores/contraseña');
            }
          });
        } else {
          // Otros errores (500, red, etc.)
          console.log('❌ Error en BigQuery:', bigQueryResult);
          setIsLoading(false);
          swal(t("login.swal_error"), t("login.swal_error_conectar"), "error");
        }
      } else {
        // Usuario no existe en Alert (SAP)
        console.log('❌ Usuario no encontrado en Alert (SAP)');
        throw new Error("Usuario no encontrado en el sistema principal (SAP)");
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      swal(t("login.swal_error_datos"), t("login.swal_valide_informacion"), "warning");
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  const infoCards = [
    {
      title: t("login.manual_title"),
      desc: t("login.manual_desc"),
      image: localStorage.lenguaje === "es" ? proveedorlogo : proveedorlogoen,
      url: "https://drive.google.com/file/d/1frwotLTJeq9xqCm6nSZbQG-jvBV_7e3U"
    },
    {
      title: t("login.ser_proveedor_title"),
      desc: t("login.ser_proveedor_desc"),
      image: localStorage.lenguaje === "es" ? comoserproveedor : comoserproveedoren,
      url: "https://docs.google.com/forms/d/1PDT_AZoTtqCwmAsruqpzg41e96YVwfNVXIWASZrkmVE"
    },
    {
      title: t("login.informacion_title"),
      desc: t("login.informacion_desc"),
      image: localStorage.lenguaje === "es" ? informacion : informacionen,
      url: "https://drive.google.com/file/d/1X2RnUsTESWlEEik9kjXk69xwhm-WKjcE/view?usp=sharing"
    },
    {
      title: t("login.tutorial_title"),
      desc: t("login.tutorial_desc"),
      image: localStorage.lenguaje === "es" ? tutorial : tutorialen,
      url: "https://www.youtube.com/watch?v=T8Ur5naLuiQ&list=PL4GjjWz8eNze-KnqZzInF35jOrU3LWTAy"
    }
  ];



  return (
    <ThemeProvider theme={loginTheme}>
      <Grid container component="main" sx={{ minHeight: '100vh', overflowX: 'hidden' }}>
        <style>
          {`
            @keyframes kenburns {
              0% { transform: scale(1); }
              100% { transform: scale(1.1); }
            }
          `}
        </style>
        <CssBaseline />

        {/* --- HERO / LOGIN SECTION --- */}
        <Grid container sx={{ height: { xs: 'auto', md: '100vh' } }}>

          {/* LEFT SIDE: BRANDING */}
          <Slide direction="right" in={animate} timeout={800}>
            <Grid
              item
              xs={false}
              sm={6}
              md={6}
              sx={{
                position: 'relative',
                display: 'flex', // To center content
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                minHeight: { xs: '300px', md: 'auto' },
              }}
            >
              {/* Background Image Layer with Animation */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${talsaBuilding})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  zIndex: 0,
                  animation: 'kenburns 20s ease-in-out infinite alternate',
                }}
              />

              {/* Gradient Overlay Layer */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(189,31,52,0.7) 0%, rgba(44,62,80,0.6) 100%)',
                  zIndex: 1
                }}
              />

              <Box sx={{ position: 'relative', zIndex: 2, color: 'white', px: 6, maxWidth: '800px' }}>
                <Fade in={animate} timeout={1500}>
                  <Typography variant="h3" component="h1" gutterBottom
                    sx={{
                      textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                      mb: 2,
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' }
                    }}
                  >
                    {t("login.titulo") || "Portal de Proveedores"}
                  </Typography>
                </Fade>
              </Box>
            </Grid>
          </Slide>

          {/* RIGHT SIDE: FORM */}
          <Slide direction="left" in={animate} timeout={800}>
            <Grid item xs={12} sm={6} md={6} component={Paper} elevation={0} square
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  my: { xs: 4, sm: 6, md: 8 },
                  mx: { xs: 2, sm: 4 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  maxWidth: { xs: '360px', sm: '420px' },
                  width: '100%'
                }}
              >
                <Grow in={animate} timeout={1000}>
                  <Box sx={{ mb: { xs: 3, sm: 5 }, textAlign: 'center' }}>
                    <img src={logopng} alt="Logo" style={{ maxHeight: '8vh', minHeight: '50px', maxWidth: '100%', filter: 'drop-shadow(0px 4px 6px rgba(0,0,0,0.1))' }} />
                  </Box>
                </Grow>

                <Fade in={animate} timeout={1200}>
                  <Box sx={{ width: '100%' }}>
                    <Typography component="h1" variant="h4" sx={{ mb: 1, color: 'text.primary', textAlign: 'center', fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                      {t("login.login") || "Bienvenido"}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                      {t("login.subtitulo")}
                    </Typography>

                    <Box component="div" sx={{ mt: 1, width: '100%' }}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label={t("login.usuario")}
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={usuario}
                        onChange={(e) => setUsuario(e.target.value)}
                        onKeyPress={handleKeyPress}
                        variant="outlined"
                      />
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label={t("login.contraseña")}
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        value={contrasena}
                        onChange={(e) => setContrasena(e.target.value)}
                        onKeyPress={handleKeyPress}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => navigate('/proveedores/recuperar-contrasena')}
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            textDecoration: 'none',
                            transition: 'color 0.2s',
                            '&:hover': { color: 'primary.main', textDecoration: 'none' }
                          }}
                        >
                          {t("login.recuperar") || "¿Olvidaste tu contraseña?"}
                        </Link>
                      </Box>

                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        sx={{
                          mt: 1,
                          mb: 2,
                          py: 1.5,
                          fontSize: '1rem',
                        }}
                        onClick={handleLogin}
                        disabled={isLoading}
                        endIcon={!isLoading && <ArrowForward />}
                      >
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : t("login.ingresar")}
                      </Button>

                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem' }}>
                          {t("login.soporte_whatsapp")}
                        </Typography>
                      </Box>

                      {/* Activation link */}
                      <Box sx={{
                        mt: 3, mb: 1, textAlign: 'center',
                        p: 2,
                        border: '1.5px dashed #BD1F34',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(189,31,52,0.04)'
                      }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, fontSize: '0.82rem' }}>
                          {t("login.primera_vez") || "¿Primera vez en el portal?"}
                        </Typography>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => navigate('/proveedores/contraseña')}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: '#BD1F34',
                            fontWeight: 700,
                            fontSize: '0.92rem',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          <LockResetIcon sx={{ fontSize: 16 }} />
                          {t("login.activa_aqui") || "Activa tu cuenta aquí"}
                        </Link>
                      </Box>

                      <Grid container alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
                        <Grid item>
                          <CambiarIdioma />
                        </Grid>
                      </Grid>
                    </Box>

                    {/* --- 3D COVERFLOW CAROUSEL --- */}
                    <CoverFlowCarousel cards={infoCards} />

                  </Box>
                </Fade>
              </Box>
            </Grid>
          </Slide>
        </Grid>

        {/* --- FOOTER SECTION --- */}
        <Box sx={{ width: '100%', bgcolor: '#F7F9FC', borderTop: '1px solid #E0E0E0', pt: 6, pb: 4 }}>
          <Box sx={{ px: 3 }}>
            <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, fontWeight: 600, color: 'text.primary' }}>

            </Typography>
            <Box sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
              maxWidth: '100%'
            }}>
              {locations.map((loc, index) => (
                <Box key={index} sx={{ flex: '1 1 auto', minWidth: '180px', maxWidth: '220px' }}>
                  <Link href={loc.url} target="_blank" underline="none">
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        textAlign: 'left',
                        transition: 'all 0.3s ease',
                        border: '1px solid #E0E0E0',
                        borderRadius: '12px',
                        height: '100%',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(189, 31, 52, 0.12)',
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1.5,
                            color: 'white',
                            fontSize: '1.2rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {loc.name.charAt(0)}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1rem' }}>
                          {loc.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 0.5 }}>
                        {loc.address}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        {loc.city}
                      </Typography>
                    </Paper>
                  </Link>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid #EAEAEA', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              © {new Date().getFullYear()} Talsa S.A.S. {t("login.derechos")}
            </Typography>
          </Box>
        </Box>

        {/* Floating WhatsApp Button */}
        <Box sx={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1000 }}>
          <WhatsAppv1 />
        </Box>


      </Grid>
    </ThemeProvider>
  );
}
